import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { BillingServiceOverview } from "./billing-service-overview.model";
import { BillingServiceOverviewStore } from "./billing-service-overview.store";
import {
  parsePersistedMinimizedPosition,
  UsageOverviewMinimizedPosition,
} from "./usage-overview-minimized/usage-overview-minimized-position.helper";

const USAGE_OVERVIEW_MINIMIZED_STORAGE_KEY = "Treeslo_usageOverviewMinimized";
const USAGE_OVERVIEW_POSITION_STORAGE_KEY = "Treeslo_usageOverviewPosition";
const USAGE_OVERVIEW_PINNED_STORAGE_KEY = "Treeslo_usageOverviewPinnedToSidenav";
const USAGE_OVERVIEW_ROUTE = "/usage-overview";

@Injectable({
  providedIn: "root",
})
export class UsageOverviewDialogService {
  private readonly router = inject(Router);
  private readonly overviewStore = inject(BillingServiceOverviewStore);

  private persistedStateRestored = false;

  readonly overview = this.overviewStore.overview;
  readonly loading = this.overviewStore.isBusy;
  readonly lastUpdated = this.overviewStore.lastUpdated;
  readonly error = this.overviewStore.hasLoadError;

  readonly minimized = signal(false);
  readonly pinnedToSidenav = signal(false);

  /** Minimized float or pinned sidenav widget is visible. */
  readonly isFloatingWidgetVisible = computed(
    () => this.pinnedToSidenav() || this.minimized(),
  );

  open(): void {
    this.setMinimized(false);
    this.navigateToPage();
  }

  restore(): void {
    this.setMinimized(false);
    this.navigateToPage();
  }

  minimize(): void {
    this.setPinnedToSidenav(false);
    this.setMinimized(true);
    this.navigateAwayFromPage();
  }

  pinToSidenav(): void {
    this.setMinimized(false);
    this.setPinnedToSidenav(true);
    this.navigateAwayFromPage();
  }

  unpinFromSidenav(): void {
    this.setPinnedToSidenav(false);
  }

  close(): void {
    this.setMinimized(false);
    this.setPinnedToSidenav(false);
    this.navigateAwayFromPage();
  }

  dismissFloating(): void {
    this.setMinimized(false);
  }

  /** Reloads GET /billing/service-overview from the API. */
  refresh(): Observable<BillingServiceOverview | null> {
    return this.overviewStore.forceLoadFromApi();
  }

  /**
   * True when usage UI needs fresh service-overview (widgets or `/usage-overview` route).
   */
  shouldRefreshServiceOverview(): boolean {
    return this.isFloatingWidgetVisible() || this.isOnUsageOverviewPage();
  }

  /**
   * GET /billing/service-overview only when {@link shouldRefreshServiceOverview}.
   * Skips the network call when widgets are hidden and the user is not on Usage Overview.
   */
  refreshServiceOverviewIfNeeded(): Observable<BillingServiceOverview | null> {
    if (!this.shouldRefreshServiceOverview()) {
      return of(this.overviewStore.overview());
    }

    return this.overviewStore.forceLoadFromApi();
  }

  openFromCache(): void {
    this.setMinimized(false);
    this.navigateToPage();
  }

  getPersistedMinimizedPosition(): UsageOverviewMinimizedPosition | null {
    try {
      return parsePersistedMinimizedPosition(
        localStorage.getItem(USAGE_OVERVIEW_POSITION_STORAGE_KEY),
      );
    } catch {
      return null;
    }
  }

  saveMinimizedPosition(position: UsageOverviewMinimizedPosition): void {
    try {
      localStorage.setItem(
        USAGE_OVERVIEW_POSITION_STORAGE_KEY,
        JSON.stringify(position),
      );
    } catch {
      // Ignore storage failures.
    }
  }

  restorePersistedStateIfNeeded(): void {
    if (this.persistedStateRestored) {
      return;
    }

    this.persistedStateRestored = true;

    if (this.readPersistedPinnedState()) {
      this.pinnedToSidenav.set(true);
      this.ensureOverviewLoadedIfEmpty();
      return;
    }

    if (!this.readPersistedMinimizedState()) {
      return;
    }

    this.minimized.set(true);
    this.ensureOverviewLoadedIfEmpty();
  }

  private ensureOverviewLoadedIfEmpty(): void {
    if (this.overviewStore.overview()) {
      return;
    }

    this.overviewStore.load().subscribe();
  }

  resetOnSignOut(): void {
    this.persistedStateRestored = false;
    this.minimized.set(false);
    this.pinnedToSidenav.set(false);
    this.overviewStore.reset();
    this.clearPersistedMinimizedState();
    this.clearPersistedMinimizedPosition();
    this.clearPersistedPinnedState();
  }

  private setMinimized(value: boolean): void {
    this.minimized.set(value);
    this.persistMinimizedState(value);

    if (value) {
      this.pinnedToSidenav.set(false);
      this.clearPersistedPinnedState();
    }
  }

  private setPinnedToSidenav(value: boolean): void {
    this.pinnedToSidenav.set(value);
    this.persistPinnedState(value);

    if (value) {
      this.minimized.set(false);
      this.clearPersistedMinimizedState();
    }
  }

  private navigateToPage(): void {
    if (this.isOnUsageOverviewPage()) {
      this.overviewStore.load().subscribe();
      return;
    }

    void this.router.navigate([USAGE_OVERVIEW_ROUTE]);
  }

  private navigateAwayFromPage(): void {
    if (!this.isOnUsageOverviewPage()) {
      return;
    }

    void this.router.navigate(["/dashboard"]);
  }

  private isOnUsageOverviewPage(): boolean {
    return this.router.url.split("?")[0].split("#")[0] === USAGE_OVERVIEW_ROUTE;
  }

  private readPersistedMinimizedState(): boolean {
    try {
      return localStorage.getItem(USAGE_OVERVIEW_MINIMIZED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  private persistMinimizedState(value: boolean): void {
    try {
      if (value) {
        localStorage.setItem(USAGE_OVERVIEW_MINIMIZED_STORAGE_KEY, "true");
      } else {
        this.clearPersistedMinimizedState();
      }
    } catch {
      // Ignore storage failures (private browsing, quota, etc.).
    }
  }

  private clearPersistedMinimizedState(): void {
    try {
      localStorage.removeItem(USAGE_OVERVIEW_MINIMIZED_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private clearPersistedMinimizedPosition(): void {
    try {
      localStorage.removeItem(USAGE_OVERVIEW_POSITION_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private readPersistedPinnedState(): boolean {
    try {
      return localStorage.getItem(USAGE_OVERVIEW_PINNED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  private persistPinnedState(value: boolean): void {
    try {
      if (value) {
        localStorage.setItem(USAGE_OVERVIEW_PINNED_STORAGE_KEY, "true");
      } else {
        this.clearPersistedPinnedState();
      }
    } catch {
      // Ignore storage failures (private browsing, quota, etc.).
    }
  }

  private clearPersistedPinnedState(): void {
    try {
      localStorage.removeItem(USAGE_OVERVIEW_PINNED_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }
}
